/** Shared auth cookie names and builders (no imports from api.ts). */

/** localStorage/sessionStorage key (origin-scoped, so it never crosses environments). */
export const TOKEN_STORAGE_KEY = 'EnthiosHub_jwt';

export const JWT_COOKIE_NAME_PROD = 'EnthiosHub_jwt';
export const JWT_COOKIE_NAME_STAGING = 'EnthiosHub_staging_jwt';
export const ACCESS_COOKIE_NAME_PROD = 'enthioshub_token';
export const ACCESS_COOKIE_NAME_STAGING = 'enthioshub_staging_token';

/*
 * Auth cookies are shared across *.enthioshub.com, so staging and production must never read
 * each other's cookies. Each environment reads and writes only its own names; there is no
 * cross-environment fallback.
 */
export function isStagingHost(host: string | null | undefined): boolean {
  return typeof host === 'string' && host.toLowerCase().includes('staging');
}

export function accessCookieNameForHost(host: string | null | undefined): string {
  return isStagingHost(host) ? ACCESS_COOKIE_NAME_STAGING : ACCESS_COOKIE_NAME_PROD;
}

export function jwtCookieNameForHost(host: string | null | undefined): string {
  return isStagingHost(host) ? JWT_COOKIE_NAME_STAGING : JWT_COOKIE_NAME_PROD;
}

function currentHost(): string {
  return typeof window !== 'undefined' ? window.location.hostname : '';
}

export function getAccessCookieName(): string {
  return accessCookieNameForHost(currentHost());
}

export function getJwtCookieName(): string {
  return jwtCookieNameForHost(currentHost());
}

export const ACCESS_COOKIE_NAME = getAccessCookieName();

const AUTH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60;

function sharedDomainAttribute(): string {
  const isEnthios = typeof window !== 'undefined'
    ? window.location.hostname.endsWith('enthioshub.com')
    : false;
  return isEnthios ? '; Domain=.enthioshub.com' : '';
}

export function authCookieOptions(): string {
  const isHttps = typeof window !== 'undefined'
    ? window.location.protocol === 'https:'
    : false;
  const secure = isHttps ? '; Secure' : '';
  return `Path=/; SameSite=Lax; Max-Age=${AUTH_COOKIE_MAX_AGE_SECONDS}${sharedDomainAttribute()}${secure}`;
}

export function buildAuthCookie(token: string): string {
  return `${getJwtCookieName()}=${encodeURIComponent(token)}; ${authCookieOptions()}`;
}

export function buildClearAuthCookie(): string {
  return `${getJwtCookieName()}=; Path=/; Max-Age=0; SameSite=Lax${sharedDomainAttribute()}`;
}

/** Read the JS-readable middleware cookie written after login (not the HttpOnly access cookie). */
export function readAuthCookieFromDocument(cookieSource?: string): string | null {
  const source = cookieSource
    ?? (typeof document !== 'undefined' ? document.cookie : '');
  if (!source) return null;
  const parts = source.split(';');
  const candidateKeys = [getJwtCookieName(), getAccessCookieName()];

  for (const part of parts) {
    const [rawName, ...rest] = part.trim().split('=');
    if (!candidateKeys.includes(rawName)) continue;
    const value = decodeURIComponent(rest.join('=').trim());
    if (value.length > 20) {
      return value;
    }
  }
  return null;
}
